import { Button } from '@neo/index'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { X } from 'lucide-react'
import { AudioUploadCore } from '../audio-prompt/audio-upload-core'
import { NodevarList } from '../nodevar-list'

export interface HoldMusicValue {
  url?: string
  file?: string
}

export interface HoldMusicFieldProps {
  label: string
  value: HoldMusicValue | undefined
  onChange: (value: HoldMusicValue) => void
  nodeVars?: string[]
  showNodeVars?: boolean
  className?: string
}

export const HoldMusicField = ({
  label,
  value,
  onChange,
  nodeVars = [],
  showNodeVars = false,
  className,
}: HoldMusicFieldProps) => {
  const url = value?.url ?? ''

  const handleUrlChange = (newUrl: string, fileName?: string) => {
    onChange({
      url: newUrl,
      file: fileName || value?.file || '',
    })
  }

  const handleClear = () => {
    onChange({
      url: '',
      file: '',
    })
  }

  // If there's a URL with audio, show the player
  if (url && !url.startsWith('{{') && (url.startsWith('https://') || url.startsWith('http://'))) {
    return (
      <div className={className}>
        <Label className="mb-1.5 block">{label}</Label>
        <div className="p-3 flex gap-5 border border-border bg-card rounded place-items-center">
          <div className="min-w-0 flex-1">
            <audio
              src={url}
              controls
              className="w-full max-w-full dark:[color-scheme:dark]"
              data-testid="audio-hold-music"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={handleClear}
            data-testid="button-clear-hold-music"
          >
            <X size={20} />
          </Button>
        </div>
      </div>
    )
  }

  // If there's a nodevar value, show input with nodevar
  if (url && url.startsWith('{{')) {
    return (
      <div className={className}>
        <Label className="mb-1.5 block">{label}</Label>
        <Input
          value={url}
          inputClassName="font-jetbrains text-xs-400"
          onChange={(e) => handleUrlChange(e.target.value)}
          endIcon={
            <div className="flex items-center gap-1">
              {showNodeVars && (
                <NodevarList
                  nodevars={nodeVars}
                  className="pr-2"
                  inputElementSelector={`[data-hold-music="${CSS.escape(label)}"]`}
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={handleClear}
                data-testid="button-clear-hold-music-nodevar"
              >
                <X size={16} />
              </Button>
            </div>
          }
          data-hold-music={label}
          data-testid="input-hold-music-url"
        />
      </div>
    )
  }

  // Show upload UI
  return (
    <div className={className}>
      <Label className="mb-1.5 block">{label}</Label>
      <AudioUploadCore
        url={url}
        onURLChange={handleUrlChange}
        nodeVars={showNodeVars ? nodeVars : undefined}
        inputId={`hold-music-${label
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')}`}
      />
    </div>
  )
}
